import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Users, MapPin, Phone, Mail, Briefcase, FileText,
  CreditCard, ChevronRight, Calendar, Euro, AlertTriangle, CheckCircle,
  Clock, Building2, User, ExternalLink
} from 'lucide-react';
import {
  getClientById, getAffairesByClient, getDevisByClient, getFacturesByClient,
  getIngenieurById, getSocieteById,
  statutDevisLabels, statutAffaireLabels, statutFactureLabels, typeFactureLabels
} from '../data/mockData';
import { useFilters } from '../App';

const ClientDetail = () => {
  const { id } = useParams();
  const client = getClientById(parseInt(id));
  const [activeTab, setActiveTab] = useState('affaires');
  const filters = useFilters();

  if (!client) {
    return (
      <div className="page-container">
        <Link to="/clients" className="btn-back"><ArrowLeft size={18} /> Retour aux clients</Link>
        <div className="empty-state">
          <Users size={48} />
          <h3>Client introuvable</h3>
        </div>
      </div>
    );
  }

  const affairesClient = getAffairesByClient(client.id, filters);
  const devisClient = getDevisByClient(client.id, filters);
  const facturesClient = getFacturesByClient(client.id, filters);

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(montant);
  };

  const montantTotalAffaires = affairesClient.reduce((acc, a) => acc + a.montantHT, 0);
  const facturesEnRetard = facturesClient.filter(f => f.statut === 'en_retard');
  const facturesEnCours = facturesClient.filter(f => f.statut === 'envoyee');
  const devisEnAttente = devisClient.filter(d => d.statut === 'envoye');

  const tabs = [
    { key: 'affaires', label: 'Affaires en cours', icon: Briefcase, count: affairesClient.filter(a => a.statut === 'en_cours').length },
    { key: 'devis', label: 'Devis', icon: FileText, count: devisClient.length },
    { key: 'facturation', label: 'Facturation', icon: CreditCard, count: facturesClient.length },
  ];

  return (
    <div className="page-container">
      <Link to="/clients" className="btn-back"><ArrowLeft size={18} /> Retour aux clients</Link>

      {/* En-tête client */}
      <div style={{
        background: 'white', borderRadius: 'var(--radius-lg)', border: '1px solid var(--gray-200)',
        padding: '2rem', marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
          <div className="famille-avatar" style={{
            width: '64px', height: '64px', fontSize: '1.75rem',
            background: client.marchePublic
              ? 'linear-gradient(135deg, #2563EB, #1E40AF)'
              : 'linear-gradient(135deg, #F97316, #EA580C)'
          }}>
            {client.nom.charAt(0)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{client.nom}</h1>
              <span className={`badge ${client.marchePublic ? 'badge-primary' : 'badge-warning'}`}>
                {client.marchePublic ? 'Marché public' : 'Client privé'}
              </span>
              {/* Sociétés associées */}
              {client.societesIds.map(sid => {
                const s = getSocieteById(sid);
                return (
                  <span key={sid} className="badge" style={{
                    background: sid === 1 ? '#DBEAFE' : '#FFF7ED',
                    color: sid === 1 ? '#1E40AF' : '#9A3412',
                  }}>
                    <Building2 size={12} style={{ marginRight: '0.25rem' }} />{s?.nom}
                  </span>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <MapPin size={14} /> {client.adresse}
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase' }}>Total affaires</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--primary)' }}>{formatMontant(montantTotalAffaires)}</div>
          </div>
        </div>

        {/* Contacts */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--gray-200)' }}>
          <div>
            <h4 style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Contact technique</h4>
            <p style={{ margin: '0 0 0.25rem', fontWeight: 600 }}>{client.contactTechnique.nom}</p>
            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Phone size={12} /> {client.contactTechnique.telephone}
            </p>
            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Mail size={12} /> {client.contactTechnique.email}
            </p>
          </div>
          <div>
            <h4 style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Contact administratif</h4>
            <p style={{ margin: '0 0 0.25rem', fontWeight: 600 }}>{client.contactAdministratif.nom}</p>
            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Phone size={12} /> {client.contactAdministratif.telephone}
            </p>
            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Mail size={12} /> {client.contactAdministratif.email}
            </p>
          </div>
          <div>
            <h4 style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Contact facturation</h4>
            <p style={{ margin: '0 0 0.25rem', fontWeight: 600 }}>{client.contactFacturation.nom}</p>
            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Phone size={12} /> {client.contactFacturation.telephone}
            </p>
            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Mail size={12} /> {client.contactFacturation.email}
            </p>
          </div>
        </div>
      </div>

      {/* Alertes */}
      {(facturesEnRetard.length > 0 || devisEnAttente.length > 0) && (
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
          {facturesEnRetard.length > 0 && (
            <div className="alert-item alert-danger" style={{ flex: 1, padding: '1rem', borderRadius: 'var(--radius)' }}>
              <AlertTriangle size={18} />
              <div>
                <p style={{ margin: 0, fontWeight: 600 }}>{facturesEnRetard.length} facture{facturesEnRetard.length > 1 ? 's' : ''} en retard</p>
                <span style={{ fontSize: '0.8125rem' }}>
                  Total : {formatMontant(facturesEnRetard.reduce((acc, f) => acc + f.montantTTC, 0))} TTC
                </span>
              </div>
            </div>
          )}
          {devisEnAttente.length > 0 && (
            <div className="alert-item alert-warning" style={{ flex: 1, padding: '1rem', borderRadius: 'var(--radius)' }}>
              <Clock size={18} />
              <div>
                <p style={{ margin: 0, fontWeight: 600 }}>{devisEnAttente.length} devis en attente de validation</p>
                <span style={{ fontSize: '0.8125rem' }}>
                  Total : {formatMontant(devisEnAttente.reduce((acc, d) => acc + d.montantHT, 0))} HT
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Onglets */}
      <div className="tabs-container">
        <div className="tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                className={`tab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <Icon size={16} />
                {tab.label}
                {tab.count !== undefined && <span className="tab-count">{tab.count}</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="tab-content">
        {/* Affaires */}
        {activeTab === 'affaires' && (
          <div style={{ overflowX: 'auto' }}>
            <table className="factures-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>N° Affaire</th>
                  <th>Désignation</th>
                  <th>Société</th>
                  <th>Adresse</th>
                  <th>Ingénieur</th>
                  <th>Honoraires</th>
                  <th>Avancement</th>
                  <th>Statut</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {affairesClient.map(a => {
                  const ing = getIngenieurById(a.ingenieurId);
                  const societe = getSocieteById(a.societeId);
                  const statut = statutAffaireLabels[a.statut];
                  return (
                    <tr key={a.id}>
                      <td><span className="facture-numero-cell">{a.numero}</span></td>
                      <td style={{ fontWeight: 500 }}>{a.designation}</td>
                      <td>
                        <span className="badge" style={{
                          background: societe?.id === 1 ? '#DBEAFE' : '#FFF7ED',
                          color: societe?.id === 1 ? '#1E40AF' : '#9A3412',
                          fontSize: '0.625rem'
                        }}>
                          {societe?.nom}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.8125rem', color: 'var(--gray-600)' }}>{a.ville}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div className="user-avatar user-avatar-xs" style={{ background: ing?.couleur }}>{ing?.initiales}</div>
                          <span style={{ fontSize: '0.8125rem' }}>{ing?.prenom} {ing?.nom}</span>
                        </div>
                      </td>
                      <td style={{ fontWeight: 600 }}>{formatMontant(a.montantHT)}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div className="progress-bar" style={{ width: '60px', height: '4px' }}>
                            <div className="progress-fill" style={{ width: `${a.avancement}%` }}></div>
                          </div>
                          <span style={{ fontSize: '0.75rem' }}>{a.avancement}%</span>
                        </div>
                      </td>
                      <td><span className="statut-badge" style={{ background: statut.bg, color: statut.color }}>{statut.label}</span></td>
                      <td>
                        <Link to={`/affaires/${a.id}`} className="btn btn-xs btn-secondary">
                          Voir <ChevronRight size={12} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {affairesClient.length === 0 && (
              <div className="empty-state" style={{ padding: '2rem' }}>
                <Briefcase size={36} />
                <h3>Aucune affaire</h3>
              </div>
            )}
          </div>
        )}

        {/* Devis */}
        {activeTab === 'devis' && (
          <div style={{ overflowX: 'auto' }}>
            <table className="factures-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>N° Devis</th>
                  <th>Désignation</th>
                  <th>Société</th>
                  <th>Date envoi</th>
                  <th>Ingénieur</th>
                  <th>Honoraires</th>
                  <th>Statut</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {devisClient.map(d => {
                  const ing = getIngenieurById(d.ingenieurId);
                  const societe = getSocieteById(d.societeId);
                  const statut = statutDevisLabels[d.statut];
                  return (
                    <tr key={d.id}>
                      <td><span className="facture-numero-cell">{d.numero}</span></td>
                      <td style={{ fontWeight: 500 }}>{d.designation}</td>
                      <td>
                        <span className="badge" style={{
                          background: societe?.id === 1 ? '#DBEAFE' : '#FFF7ED',
                          color: societe?.id === 1 ? '#1E40AF' : '#9A3412',
                          fontSize: '0.625rem'
                        }}>
                          {societe?.nom}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.8125rem', color: 'var(--gray-600)' }}>
                        {d.dateEnvoi ? new Date(d.dateEnvoi).toLocaleDateString('fr-FR') : '-'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div className="user-avatar user-avatar-xs" style={{ background: ing?.couleur }}>{ing?.initiales}</div>
                          <span style={{ fontSize: '0.8125rem' }}>{ing?.prenom} {ing?.nom}</span>
                        </div>
                      </td>
                      <td style={{ fontWeight: 600 }}>{formatMontant(d.montantHT)}</td>
                      <td><span className="statut-badge" style={{ background: statut.bg, color: statut.color }}>{statut.label}</span></td>
                      <td>
                        <Link to={`/devis/${d.id}`} className="btn btn-xs btn-secondary">
                          Détails <ChevronRight size={12} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {devisClient.length === 0 && (
              <div className="empty-state" style={{ padding: '2rem' }}>
                <FileText size={36} />
                <h3>Aucun devis</h3>
              </div>
            )}
          </div>
        )}

        {/* Facturation */}
        {activeTab === 'facturation' && (
          <div>
            {/* Résumé facturation */}
            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '1.5rem' }}>
              <div className="stat-card stat-success">
                <div className="stat-icon"><CheckCircle size={20} /></div>
                <div className="stat-content">
                  <p className="stat-label">Payé</p>
                  <p className="stat-value" style={{ fontSize: '1.25rem' }}>
                    {formatMontant(facturesClient.filter(f => f.statut === 'payee').reduce((acc, f) => acc + f.montantTTC, 0))}
                  </p>
                </div>
              </div>
              <div className="stat-card stat-primary">
                <div className="stat-icon"><Clock size={20} /></div>
                <div className="stat-content">
                  <p className="stat-label">En attente</p>
                  <p className="stat-value" style={{ fontSize: '1.25rem' }}>
                    {formatMontant(facturesEnCours.reduce((acc, f) => acc + f.montantTTC, 0))}
                  </p>
                </div>
              </div>
              <div className="stat-card" style={{ border: facturesEnRetard.length > 0 ? '2px solid var(--danger)' : undefined }}>
                <div className="stat-icon" style={{ background: '#FEE2E2', color: '#DC2626' }}><AlertTriangle size={20} /></div>
                <div className="stat-content">
                  <p className="stat-label">En retard</p>
                  <p className="stat-value" style={{ fontSize: '1.25rem', color: facturesEnRetard.length > 0 ? 'var(--danger)' : undefined }}>
                    {formatMontant(facturesEnRetard.reduce((acc, f) => acc + f.montantTTC, 0))}
                  </p>
                </div>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="factures-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>N° Facture</th>
                    <th>Société</th>
                    <th>Type</th>
                    <th>Date envoi</th>
                    <th>Échéance</th>
                    <th>Montant HT</th>
                    <th>Montant TTC</th>
                    <th>Statut</th>
                    <th>Paiement</th>
                  </tr>
                </thead>
                <tbody>
                  {facturesClient.map(f => {
                    const societe = getSocieteById(f.societeId);
                    const fStatut = statutFactureLabels[f.statut];
                    const fType = typeFactureLabels[f.type];
                    return (
                      <tr key={f.id} style={f.statut === 'en_retard' ? { background: '#FEE2E2' } : undefined}>
                        <td><span className="facture-numero-cell">{f.numero}</span></td>
                        <td>
                          <span className="badge" style={{
                            background: societe?.id === 1 ? '#DBEAFE' : '#FFF7ED',
                            color: societe?.id === 1 ? '#1E40AF' : '#9A3412',
                            fontSize: '0.625rem'
                          }}>
                            {societe?.nom}
                          </span>
                        </td>
                        <td><span className="statut-badge" style={{ background: fType.bg, color: fType.color }}>{fType.label}</span></td>
                        <td style={{ fontSize: '0.8125rem' }}>{f.dateEnvoi ? new Date(f.dateEnvoi).toLocaleDateString('fr-FR') : '-'}</td>
                        <td style={{ fontSize: '0.8125rem' }}>{f.dateEcheance ? new Date(f.dateEcheance).toLocaleDateString('fr-FR') : '-'}</td>
                        <td style={{ fontWeight: 600 }}>{formatMontant(f.montantHT)}</td>
                        <td>{formatMontant(f.montantTTC)}</td>
                        <td><span className="statut-badge" style={{ background: fStatut.bg, color: fStatut.color }}>{fStatut.label}</span></td>
                        <td style={{ fontSize: '0.8125rem' }}>{f.datePaiement ? new Date(f.datePaiement).toLocaleDateString('fr-FR') : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {facturesClient.length === 0 && (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  <CreditCard size={36} />
                  <h3>Aucune facture</h3>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDetail;
